import { useState, useEffect, useCallback } from 'react';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiReturn<T> extends UseApiState<T> {
  refetch: () => Promise<void>;
}

export function useApi<T>(
  apiCall: () => Promise<any>,
  dependencies: any[] = []
): UseApiReturn<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await apiCall();
      
      if (response.success) {
        setState({
          data: response.data,
          loading: false,
          error: null
        });
      } else {
        setState({
          data: null,
          loading: false,
          // Two shapes reach here and they use different fields. A transport
          // failure is caught in a service and rethrown as { error }, but a
          // business refusal arrives as HTTP 200 with { success: false,
          // message } — the API's own envelope. Reading only `error` discarded
          // every one of those and showed "An error occurred" instead.
          //
          // That is not cosmetic: a real "Access denied: You don't have
          // permission to access this organization" was reported as phone
          // numbers being missing, and the message that would have explained it
          // had already been thrown away here. Matches the precedence in
          // utils/apiErrorHandler.ts.
          error: response.error || response.message || 'An error occurred'
        });
      }
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.message || 'An error occurred'
      });
    }
  }, [apiCall]);

  useEffect(() => {
    fetchData();
  }, dependencies);

  return {
    ...state,
    refetch: fetchData
  };
}